/* ============================================================
   Partpoint Damage Analyzer — Frontend  ·  v2.3.2 (2026-10-07)
   Vanilla JS, no framework. Targets the section root via
   data-dpa-* attributes; multiple instances are supported.

   v2.3.2 (bug hunt 2026-10-07; worker 2.3.2):
     - HEIC/HEIF the browser cannot open (Chrome, Edge, Firefox) is refused
       here at once — no Turnstile token spent, nothing uploaded (worker 2.3.1
       refuses such originals: a camera HEIC carries location/device data).
       While a HEIC is checked its thumbnail says "HEIC", not a broken image.
       The step text no longer advertises HEIC (DMG-A-02).
     - the worker URL from the section settings must be https:// (http only
       for a local preview on localhost) — otherwise an explicit error and
       nothing is sent (security audit 2026-10-02, S10).
     - order view without picks: the offered options get the live price and
       stock too — the note under them says "current data" (DMG-A-10).
     - reduced motion: the jump to the results is instant; the progress bar
       moves with transform: scaleX() instead of width (no layout 8×/s);
       the WhatsApp button's shine is a transform layer (section CSS, DMG-A-09).

   v2.3.0 (audit):
     - the Turnstile token also goes in the query string (?ts=): worker 2.3
       verifies it before it reads the upload. It stays in the form too, so
       this file still works with a 2.2 worker.
     - the token is read again after the photos are prepared (a slow phone
       could send one that had expired meanwhile).
     - VIN field: I/O/Q become 1/0/0 instead of being deleted (a deleted
       letter shifted the whole VIN); the caret stays where the user types.
     - a rejected file's message is no longer wiped by a good file picked
       with it; safeUrl() accepts https:// and protocol-relative URLs only.

   v2.2.0: ONE link for the whole order instead of the Shopify cart link
     (the store has no online checkout). The worker stores what every analysis
     offered (offer_id, 30 days, no VIN); the WhatsApp message carries the order
     code and one short link  <this page>?order=<offer_id>&p=<picks>  that
     opens this page in "order view": each picked product with photo, live
     price and link, the total and the parts without a product.
     Spam guard: the button exists only after a verified analysis (Turnstile +
     daily limits); one browser sends an order at most 3 times, 60 s apart;
     the operator trusts only messages whose link opens a verified order.

   v2.1.0: order from the result — every product card has a checkbox
     (default: the best in-stock match per damaged part, or every in-stock
     product with the section setting "all"), a running total, select-all /
     clear, and "Order via WhatsApp" (data-dpa-wa, default 995500702500): the
     message carries the car + VIN, each picked product (store title in
     Georgian, price, vendor), the total and the parts without a product.

   v2.0.0 (worker v2 / Gemini 3.8 Flash):
     - ru/en pages get the analysis in their language (worker `lang`);
       severity / confidence / damage labels come localized from the worker
       (`*_label`); the Georgian legacy values stay the CSS modifiers.
     - photos are re-encoded in the browser one after another (≤1600 px
       JPEG): EXIF/GPS metadata is dropped and the camera orientation is
       applied, so left/right is judged on an upright picture
       (createImageBitmap → <img> fallback). A file the browser cannot decode
       (HEIC on desktop Chrome) is sent as-is; the worker strips JPEG metadata
       again on its side.
     - Turnstile: a visible "security check" stage waits for the token
       instead of sending an empty one; a blocked Turnstile script gets its own
       message; the widget is reset only after a token was spent.
     - client-side model-year check (data-dpa-min-year) right after the VIN
       decode — no upload for a car the service does not cover. A late NHTSA
       answer for a VIN the user already changed is ignored.
     - the analysis uses a snapshot of the decoded vehicle (editing the VIN
       mid-request cannot mix two cars), request timeout 130 s, Retry-After
       shown only where waiting helps, product links built for the current
       locale (routes.root_url), worker notes (no visible damage,
       used-original-only rear quarter panel…).
     - everything the worker returns is rendered with textContent/escaping;
       URLs pass safeUrl().
   Backward compatible with the v1 worker response.
   ============================================================ */
(function () {
  'use strict';

  var NHTSA_DECODE = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/';
  var ALLOWED_MAKES = { HYUNDAI: 1, KIA: 1 };
  var MAX_IMAGES = 2;
  var MAX_PICK_BYTES = 30 * 1024 * 1024; // original file sanity cap (it is downscaled before upload)
  var MAX_UPLOAD_BYTES = 7 * 1024 * 1024; // per photo after downscale (worker limit)
  var MAX_UPLOAD_TOTAL = 10 * 1024 * 1024; // all photos after downscale (worker limit)
  var ACCEPTED = /^image\/(jpeg|png|webp|heic|heif)$/;
  var HEIC_TYPE = /^image\/hei[cf](-sequence)?$/;
  var MAX_EDGE = 1600;
  var JPEG_QUALITY = 0.85;
  var REQUEST_TIMEOUT_MS = 130000; // > the worker's request budget (126 s from its start, worker 2.3.2) + the answer (≤ 1.5 s per step after it)
  var TURNSTILE_WAIT_MS = 15000;
  // Retry-After is meaningful only for short waits; the daily limits say "tomorrow" in their message
  var RETRY_HINT_CODES = { BUSY: 1, RATE_LIMITED: 1, SERVICE_BUSY: 1 };
  // above this the WhatsApp text is sent with shortened product titles (very long links fail on some phones)
  var WA_MAX_URL = 4000;
  var WA_COOLDOWN_S = 60;       // between two WhatsApp sends from one browser
  var WA_MAX_PER_ORDER = 3;     // sends of one analysis' order from one browser
  var WA_STORE = 'dpa-wa-v1';
  var OFFER_ID = /^[A-Za-z0-9_-]{22}$/;
  var OFFER_CODE = /^PP-[0-9A-Z]{6}$/;
  var MAX_OFFER_IX = 511;       // the pick mask stays ≤ 128 hex characters
  var LIVE_PRICE_MAX = 30;      // order view: live price/stock lookups per page

  var DPA_I18N = {
    ka: {
      vinLen: 'VIN კოდი უნდა იყოს 17 სიმბოლო (ლათინური + ციფრები, გარდა I, O, Q)',
      checking: 'შემოწმება...',
      notOurBrand: function (make, model) { return 'ეს არის ' + make + ' ' + model + '. სამწუხაროდ, ვმუშაობთ მხოლოდ Hyundai და KIA-ს ნაწილებზე.'; },
      noDecode: 'VIN კოდი ვერ გაიშიფრა. გადაამოწმეთ კოდი და სცადეთ თავიდან.',
      partialDecode: 'VIN ნაწილობრივ გაიშიფრა. გთხოვთ, გადაამოწმოთ VIN კოდი.',
      decodeDown: 'VIN-ის გაშიფვრის სერვისი დროებით მიუწვდომელია. სცადეთ თავიდან.',
      tooOld: function (y) { return 'ეს სერვისი მუშაობს ' + y + ' წლის ან უფრო ახალ Hyundai და KIA მანქანებზე.'; },
      decode: 'გაშიფვრა',
      maxImages: function (n) { return 'მაქსიმუმ ' + n + ' ფოტოა დაშვებული.'; },
      badFormat: 'მხარდაუჭერელი ფორმატი. გამოიყენეთ JPG, PNG ან WEBP.',
      heicNo: 'HEIC ფოტოს ეს ბრაუზერი ვერ ხსნის, ორიგინალში კი მდებარეობის/მოწყობილობის მონაცემებია — ასე ვერ გავაგზავნით. გადაიღეთ ეკრანის სურათი ან შეინახეთ JPEG-ად და ატვირთეთ თავიდან.',
      tooBig: 'ფოტო ძალიან დიდია.',
      tooBigTotal: 'ფოტოების ჯამური ზომა ძალიან დიდია. აირჩიეთ უფრო მცირე ფაილი.',
      photo: 'ფოტო',
      removePhoto: 'ფოტოს მოშორება',
      noWorker: 'Worker URL არ არის კონფიგურირებული. გადადით Theme Editor-ში.',
      badWorker: 'Worker URL უნდა იწყებოდეს https://-ით — შეასწორეთ Theme Editor-ში.',
      needPhoto: 'ატვირთეთ მინიმუმ 1 ფოტო.',
      needVin: 'ჯერ გაშიფრეთ VIN კოდი.',
      confirmVehicle: 'გთხოვთ, დაადასტუროთ, რომ ფოტო ეკუთვნის VIN-ის მანქანას.',
      waitCheck: 'უსაფრთხოების შემოწმება ჯერ არ დასრულებულა — დაელოდეთ რამდენიმე წამს და სცადეთ თავიდან.',
      tsBlocked: 'უსაფრთხოების შემოწმება ვერ ჩაიტვირთა. გამორთეთ რეკლამის ბლოკერი ამ გვერდისთვის ან სცადეთ სხვა ბრაუზერით.',
      securityCheck: 'უსაფრთხოების შემოწმება…',
      httpError: function (st) { return 'შეცდომა (HTTP ' + st + ')'; },
      netFail: 'სერვერთან კავშირი ვერ მოხერხდა. შეამოწმეთ ინტერნეტი და სცადეთ თავიდან.',
      timeout: 'ანალიზს ჩვეულებრივზე მეტი დრო დასჭირდა. სცადეთ თავიდან.',
      retryIn: function (s) { return ' (სცადეთ ' + s + ' წამში)'; },
      st1: 'ფოტო მზადდება და იტვირთება…', st1log: '> ფოტოს დამუშავება და ატვირთვა…',
      st2: 'AI აანალიზებს დაზიანებას…', st2log: '> Gemini Vision · დაზიანების დეტექცია…',
      st3: 'ნაწილები იძებნება კატალოგში…', st3log: '> კატალოგის დამთხვევა · Hyundai/KIA…',
      done: 'დასრულდა ✓', doneLog: '> ანალიზი დასრულდა ✓',
      severity: 'დაზიანების დონე', impact: 'დარტყმის ზონა',
      visible: 'ხილული დაზიანებები', hidden: 'სავარაუდო შიდა დაზიანებები',
      noProduct: 'ამ ნაწილზე პროდუქტი ვერ მოიძებნა.',
      sideNote: 'მხარე (მარცხენა/მარჯვენა) ფოტოზე ზუსტად ვერ დადგინდა — ნაჩვენებია ორივე მხარის ვარიანტი. გთხოვთ, შეარჩიოთ თქვენი მანქანის შესაბამისი მხარე.',
      posNote: 'ფოტოზე ვერ დადგინდა, წინა ნაწილია თუ უკანა — ნაჩვენებია ორივე ვარიანტი.',
      noImage: 'სურათი არ არის', soldOut: 'მარაგი ამოწურულია', searchCatalog: '→ კატალოგში მოძებნა',
      pick: 'შეკვეთაში დამატება', orderTitle: 'შენი შეკვეთა',
      selectedN: function (n) { return 'არჩეულია ' + n + ' პროდუქტი'; },
      total: 'ჯამი', priceTbd: 'ზოგიერთის ფასს ოპერატორი დაგიზუსტებთ',
      selectAll: 'ყველას მონიშვნა', clearAll: 'გასუფთავება',
      waOrder: 'შეკვეთა WhatsApp-ით', waConsult: 'კონსულტაცია WhatsApp-ით',
      waHint: 'გაიხსნება WhatsApp შევსებული შეკვეთით — უბრალოდ გააგზავნეთ. ფასს და მარაგს ოპერატორი დაგიდასტურებთ.',
      mHello: 'გამარჯობა! მინდა შევუკვეთო ნაწილები AI დაზიანების ანალიზიდან (partpoint.ge).',
      mConsult: 'გამარჯობა! მჭირდება კონსულტაცია AI დაზიანების ანალიზის მიხედვით (partpoint.ge).',
      mCar: 'მანქანა', mItems: 'არჩეული პროდუქტები', mTotal: 'ჯამი', mTbd: 'ფასი დასაზუსტებელია',
      mSoldOut: 'მარაგში არ არის', mNoProduct: 'დაზიანებული ნაწილები არჩეული პროდუქტის გარეშე',
      mNotFound: 'კატალოგში ვერ მოიძებნა', mUsed: 'მეორადი ორიგინალი',
      mNotSelected: 'არ არის არჩეული', mSideTbd: 'მხარე დასაზუსტებელია', mLikely: 'სავარაუდო',
      mSeverity: 'დაზიანება', mOrder: 'შეკვეთა',
      waSent: 'გაგზავნილია ✓', waAgain: function (s) { return 'ხელახლა გაგზავნა ' + s + ' წამში'; },
      waDone: 'ოპერატორი მალე დაგიკავშირდებათ',
      waLimit: 'ეს შეკვეთა უკვე გაგზავნილია. ცვლილებისთვის მიწერეთ ოპერატორს იმავე WhatsApp ჩატში.',
      ofLoading: 'შეკვეთა იტვირთება…', ofVerified: 'დადასტურებული AI ანალიზი · partpoint.ge', ofTitle: 'შეკვეთა',
      ofNotFound: 'შეკვეთა ვერ მოიძებნა ან ვადა გაუვიდა (ბმული 30 დღე მოქმედებს).',
      ofError: 'შეკვეთა ვერ ჩაიტვირთა. შეამოწმეთ ინტერნეტი და განაახლეთ გვერდი.',
      ofPriceNow: 'ფასი განახლდა', ofGone: 'აღარ არის საიტზე', ofNone: 'პროდუქტი არ არის არჩეული — ქვემოთ ანალიზის ყველა ვარიანტია.',
      ofOptions: 'ანალიზის შემოთავაზებული ვარიანტები',
      ofNote: 'ფასი და მარაგი ნაჩვენებია საიტის მიმდინარე მონაცემებით; საბოლოოდ ადასტურებს ოპერატორი.',
      ofNew: 'ახალი ანალიზი'
    },
    ru: {
      vinLen: 'VIN-код должен содержать 17 символов (латиница + цифры, кроме I, O, Q)',
      checking: 'Проверка...',
      notOurBrand: function (make, model) { return 'Это ' + make + ' ' + model + '. К сожалению, мы работаем только с запчастями Hyundai и KIA.'; },
      noDecode: 'VIN-код не удалось расшифровать. Проверьте код и попробуйте снова.',
      partialDecode: 'VIN расшифрован частично. Пожалуйста, проверьте VIN-код.',
      decodeDown: 'Сервис расшифровки VIN временно недоступен. Попробуйте снова.',
      tooOld: function (y) { return 'Сервис работает с автомобилями Hyundai и KIA ' + y + ' года и новее.'; },
      decode: 'Расшифровать',
      maxImages: function (n) { return 'Максимум ' + n + ' фото.'; },
      badFormat: 'Неподдерживаемый формат. Используйте JPG, PNG или WEBP.',
      heicNo: 'Этот браузер не открывает фото HEIC, а в оригинале есть данные о местоположении/устройстве — так отправить его нельзя. Сделайте скриншот или сохраните фото как JPEG и загрузите снова.',
      tooBig: 'Фото слишком большое.',
      tooBigTotal: 'Общий размер фото слишком большой. Выберите файл поменьше.',
      photo: 'Фото',
      removePhoto: 'Удалить фото',
      noWorker: 'Worker URL не настроен. Откройте Theme Editor.',
      badWorker: 'Worker URL должен начинаться с https:// — исправьте в Theme Editor.',
      needPhoto: 'Загрузите минимум 1 фото.',
      needVin: 'Сначала расшифруйте VIN-код.',
      confirmVehicle: 'Пожалуйста, подтвердите, что фото относится к автомобилю с этим VIN.',
      waitCheck: 'Проверка безопасности ещё не завершена — подождите несколько секунд и попробуйте снова.',
      tsBlocked: 'Не удалось загрузить проверку безопасности. Отключите блокировщик рекламы для этой страницы или попробуйте другой браузер.',
      securityCheck: 'Проверка безопасности…',
      httpError: function (st) { return 'Ошибка (HTTP ' + st + ')'; },
      netFail: 'Не удалось связаться с сервером. Проверьте интернет и попробуйте снова.',
      timeout: 'Анализ занял больше времени, чем обычно. Попробуйте снова.',
      retryIn: function (s) { return ' (попробуйте через ' + s + ' с)'; },
      st1: 'Подготовка и загрузка фото…', st1log: '> Обработка и загрузка изображения…',
      st2: 'AI анализирует повреждения…', st2log: '> Gemini Vision · детекция повреждений…',
      st3: 'Поиск деталей в каталоге…', st3log: '> Сопоставление с каталогом · Hyundai/KIA…',
      done: 'Готово ✓', doneLog: '> Анализ завершён ✓',
      severity: 'Степень повреждения', impact: 'Зона удара',
      visible: 'Видимые повреждения', hidden: 'Вероятные скрытые повреждения',
      noProduct: 'Для этой детали товар не найден.',
      sideNote: 'Сторону (левая/правая) по фото определить точно не удалось — показаны варианты для обеих сторон. Пожалуйста, выберите сторону, подходящую вашему автомобилю.',
      posNote: 'По фото не удалось определить, передняя это деталь или задняя — показаны оба варианта.',
      noImage: 'Нет изображения', soldOut: 'Нет в наличии', searchCatalog: '→ Найти в каталоге',
      pick: 'Добавить в заказ', orderTitle: 'Ваш заказ',
      selectedN: function (n) { return 'Выбрано товаров: ' + n; },
      total: 'Итого', priceTbd: 'цену части товаров уточнит оператор',
      selectAll: 'Выбрать все', clearAll: 'Снять все',
      waOrder: 'Заказать в WhatsApp', waConsult: 'Консультация в WhatsApp',
      waHint: 'Откроется WhatsApp с готовым заказом — просто отправьте. Цену и наличие подтвердит оператор.',
      mHello: 'Здравствуйте! Хочу заказать запчасти по AI-анализу повреждений (partpoint.ge).',
      mConsult: 'Здравствуйте! Нужна консультация по AI-анализу повреждений (partpoint.ge).',
      mCar: 'Автомобиль', mItems: 'Выбранные товары', mTotal: 'Итого', mTbd: 'цена уточняется',
      mSoldOut: 'нет в наличии', mNoProduct: 'Повреждённые детали без выбранного товара',
      mNotFound: 'не найдено в каталоге', mUsed: 'б/у оригинал',
      mNotSelected: 'не выбрано', mSideTbd: 'сторону уточнить', mLikely: 'вероятно',
      mSeverity: 'Повреждение', mOrder: 'Заказ',
      waSent: 'Отправлено ✓', waAgain: function (s) { return 'Повторно через ' + s + ' с'; },
      waDone: 'Оператор скоро свяжется с вами',
      waLimit: 'Этот заказ уже отправлен. Чтобы что-то изменить, напишите оператору в том же чате WhatsApp.',
      ofLoading: 'Загрузка заказа…', ofVerified: 'Проверенный AI-анализ · partpoint.ge', ofTitle: 'Заказ',
      ofNotFound: 'Заказ не найден или устарел (ссылка действует 30 дней).',
      ofError: 'Не удалось загрузить заказ. Проверьте интернет и обновите страницу.',
      ofPriceNow: 'цена обновлена', ofGone: 'больше нет на сайте', ofNone: 'Товары не выбраны — ниже все варианты анализа.',
      ofOptions: 'Варианты, предложенные анализом',
      ofNote: 'Цена и наличие — по текущим данным сайта; окончательно подтверждает оператор.',
      ofNew: 'Новый анализ'
    },
    en: {
      vinLen: 'The VIN must be 17 characters (letters + digits, except I, O, Q)',
      checking: 'Checking...',
      notOurBrand: function (make, model) { return 'This is a ' + make + ' ' + model + '. Unfortunately we only work with Hyundai and KIA parts.'; },
      noDecode: 'The VIN could not be decoded. Check the code and try again.',
      partialDecode: 'The VIN was only partially decoded. Please double-check the VIN.',
      decodeDown: 'The VIN decoding service is temporarily unavailable. Please try again.',
      tooOld: function (y) { return 'This service works with Hyundai and KIA cars from ' + y + ' onwards.'; },
      decode: 'Decode',
      maxImages: function (n) { return 'A maximum of ' + n + ' photos is allowed.'; },
      badFormat: 'Unsupported format. Use JPG, PNG or WEBP.',
      heicNo: 'This browser cannot open HEIC photos, and the original contains location/device data — so it cannot be sent as is. Take a screenshot or save it as JPEG and upload it again.',
      tooBig: 'The photo is too large.',
      tooBigTotal: 'The photos are too large in total. Choose a smaller file.',
      photo: 'Photo',
      removePhoto: 'Remove photo',
      noWorker: 'Worker URL is not configured. Open the Theme Editor.',
      badWorker: 'The Worker URL must start with https:// — fix it in the Theme Editor.',
      needPhoto: 'Upload at least 1 photo.',
      needVin: 'Decode the VIN first.',
      confirmVehicle: 'Please confirm that the photo belongs to the car with this VIN.',
      waitCheck: 'The security check is not finished yet — wait a few seconds and try again.',
      tsBlocked: 'The security check could not load. Disable your ad blocker for this page or try another browser.',
      securityCheck: 'Security check…',
      httpError: function (st) { return 'Error (HTTP ' + st + ')'; },
      netFail: 'Could not reach the server. Check your connection and try again.',
      timeout: 'The analysis took longer than usual. Please try again.',
      retryIn: function (s) { return ' (try again in ' + s + ' s)'; },
      st1: 'Preparing and uploading photos…', st1log: '> Processing and uploading image…',
      st2: 'AI is analysing the damage…', st2log: '> Gemini Vision · damage detection…',
      st3: 'Looking for parts in the catalog…', st3log: '> Catalog matching · Hyundai/KIA…',
      done: 'Done ✓', doneLog: '> Analysis complete ✓',
      severity: 'Damage level', impact: 'Impact area',
      visible: 'Visible damage', hidden: 'Likely hidden damage',
      noProduct: 'No product found for this part.',
      sideNote: 'The side (left/right) could not be determined from the photo — both sides are shown. Please pick the side that matches your car.',
      posNote: 'The photo does not show whether this is the front or the rear part — both are shown.',
      noImage: 'No image', soldOut: 'Out of stock', searchCatalog: '→ Search the catalog',
      pick: 'Add to order', orderTitle: 'Your order',
      selectedN: function (n) { return n + (n === 1 ? ' item selected' : ' items selected'); },
      total: 'Total', priceTbd: 'the operator will confirm some prices',
      selectAll: 'Select all', clearAll: 'Clear',
      waOrder: 'Order via WhatsApp', waConsult: 'Ask via WhatsApp',
      waHint: 'WhatsApp opens with the order already written — just send it. The operator confirms price and stock.',
      mHello: 'Hello! I would like to order parts from the AI damage analysis (partpoint.ge).',
      mConsult: 'Hello! I need advice on my AI damage analysis (partpoint.ge).',
      mCar: 'Car', mItems: 'Selected products', mTotal: 'Total', mTbd: 'price to confirm',
      mSoldOut: 'out of stock', mNoProduct: 'Damaged parts without a selected product',
      mNotFound: 'not found in the catalog', mUsed: 'used original',
      mNotSelected: 'not selected', mSideTbd: 'side to confirm', mLikely: 'likely',
      mSeverity: 'Damage', mOrder: 'Order',
      waSent: 'Sent ✓', waAgain: function (s) { return 'Send again in ' + s + ' s'; },
      waDone: 'The operator will contact you soon',
      waLimit: 'This order has already been sent. To change something, write to the operator in the same WhatsApp chat.',
      ofLoading: 'Loading the order…', ofVerified: 'Verified AI analysis · partpoint.ge', ofTitle: 'Order',
      ofNotFound: 'The order was not found or has expired (links work for 30 days).',
      ofError: 'The order could not be loaded. Check your connection and reload the page.',
      ofPriceNow: 'price updated', ofGone: 'no longer on the site', ofNone: 'No product selected — all options of the analysis are below.',
      ofOptions: 'Options offered by the analysis',
      ofNote: 'Price and stock are shown from the current site data; the operator confirms them.',
      ofNew: 'New analysis'
    }
  };

  // v1 worker compatibility: its Georgian values double as the CSS modifiers
  var SEVERITY_CLASS = { none: 'მსუბუქი', minor: 'მსუბუქი', moderate: 'საშუალო', severe: 'მძიმე' };
  var CONF_CLASS = { high: 'მაღალი', medium: 'საშუალო', low: 'დაბალი' };

  function dpaLang(root) {
    var l = String((root && root.getAttribute('data-dpa-lang')) || (document.documentElement && document.documentElement.lang) || 'ka').toLowerCase();
    return l.indexOf('ru') === 0 ? 'ru' : (l.indexOf('en') === 0 ? 'en' : 'ka');
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-dpa-root]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.__dpaInitialized) continue;
      root.__dpaInitialized = true;
      new DamageAnalyzer(root);
    }
  }

  function DamageAnalyzer(root) {
    this.root = root;
    var wurl = String(root.getAttribute('data-dpa-worker-url') || '').trim();
    // v2.3.2 (audit S10): https only (http just for a local preview) — the VIN, the photos and the token never go in clear
    this.workerUrl = workerUrlOk(wurl) ? wurl : '';
    this.workerUrlBad = !!wurl && !this.workerUrl;
    this.shopUrl = (root.getAttribute('data-dpa-shop-url') || '').replace(/\/+$/, '');
    this.rootUrl = (root.getAttribute('data-dpa-root-url') || '/').replace(/\/+$/, '');
    this.searchUrl = root.getAttribute('data-dpa-search-url') || (this.rootUrl + '/search');
    this.currency = root.getAttribute('data-dpa-currency') || 'GEL';
    this.minYear = parseInt(root.getAttribute('data-dpa-min-year') || '', 10) || 0;
    this.waNumber = waDigits(root.getAttribute('data-dpa-wa'));
    this.waIcon = safeUrl(root.getAttribute('data-dpa-wa-icon'));
    this.orderDefault = root.getAttribute('data-dpa-order-default') === 'all' ? 'all' : 'best';
    this.lang = dpaLang(root);
    this.t = DPA_I18N[this.lang] || DPA_I18N.ka;
    this.state = { vehicle: null, images: [], loading: false };
    this.cacheElements();
    this.bindEvents();
    this.updateAnalyzeButton();
    this.initOfferView(); // ?order=… — the page behind a WhatsApp order link
  }

  DamageAnalyzer.prototype.cacheElements = function () {
    var root = this.root;
    var q = function (sel) { return root.querySelector(sel); };
    this.el = {
      vinInput: q('[data-dpa-vin]'),
      vinDecodeBtn: q('[data-dpa-vin-decode]'),
      vehicleResult: q('[data-dpa-vehicle-result]'),
      vinError: q('[data-dpa-vin-error]'),
      drop: q('[data-dpa-drop]'),
      fileInput: q('[data-dpa-file]'),
      previews: q('[data-dpa-previews]'),
      confirm: q('[data-dpa-confirm]'),
      analyzeBtn: q('[data-dpa-analyze]'),
      status: q('[data-dpa-status]'),
      results: q('[data-dpa-results]'),
      turnstile: q('.cf-turnstile')
    };
  };

  DamageAnalyzer.prototype.bindEvents = function () {
    var self = this, el = this.el;
    if (el.vinInput) {
      el.vinInput.addEventListener('input', function (e) {
        var input = e.target, raw = input.value, v = cleanVin(raw);
        if (raw !== v) {
          // rewriting the value throws the caret to the end — keep it where the user is typing
          var at = typeof input.selectionStart === 'number' ? cleanVin(raw.slice(0, input.selectionStart)).length : v.length;
          input.value = v;
          try { if (document.activeElement === input) input.setSelectionRange(at, at); } catch (err) { /* no selection API */ }
        }
        if (self.state.vehicle && self.state.vehicle.vin !== v) self.clearVehicle();
        self.clearVinError();
        self.updateAnalyzeButton();
      });
      el.vinInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); self.decodeVin(); }
      });
    }
    if (el.vinDecodeBtn) el.vinDecodeBtn.addEventListener('click', function () { self.decodeVin(); });
    if (el.fileInput) {
      el.fileInput.addEventListener('change', function (e) {
        var files = e.target.files ? Array.prototype.slice.call(e.target.files) : [];
        if (files.length) self.addFiles(files);
        e.target.value = '';
      });
    }
    if (el.drop) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        el.drop.addEventListener(ev, function (e) { e.preventDefault(); el.drop.classList.add('dpa-drop--dragging'); });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        el.drop.addEventListener(ev, function (e) { e.preventDefault(); el.drop.classList.remove('dpa-drop--dragging'); });
      });
      el.drop.addEventListener('drop', function (e) {
        var files = e.dataTransfer && e.dataTransfer.files ? Array.prototype.slice.call(e.dataTransfer.files) : [];
        if (files.length) self.addFiles(files);
      });
    }
    if (el.previews) {
      el.previews.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-dpa-remove]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        self.removeImage(parseInt(btn.getAttribute('data-dpa-remove'), 10));
      });
    }
    if (el.confirm) el.confirm.addEventListener('change', function () { self.updateAnalyzeButton(); });
    if (el.analyzeBtn) el.analyzeBtn.addEventListener('click', function () { self.analyze(); });
    if (el.results) {
      // order selection: one delegated listener survives every re-render of the results
      el.results.addEventListener('change', function (e) {
        var input = e.target && e.target.closest ? e.target.closest('[data-dpa-pick-input]') : null;
        if (!input || !self._order) return;
        self.setPicked(input.getAttribute('data-dpa-pick-input'), input.checked);
      });
      el.results.addEventListener('click', function (e) {
        if (!self._order || !e.target || !e.target.closest) return;
        if (e.target.closest('[data-dpa-order-wa]')) self.onWaClick(e);
        else if (e.target.closest('[data-dpa-order-all]')) { e.preventDefault(); self.pickAll(true); }
        else if (e.target.closest('[data-dpa-order-none]')) { e.preventDefault(); self.pickAll(false); }
      });
    }
  };

  // ─── VIN decode (browser → NHTSA; the worker decodes again, authoritatively) ──
  DamageAnalyzer.prototype.decodeVin = function () {
    var self = this, t = this.t;
    var vin = (this.el.vinInput.value || '').trim().toUpperCase();
    this.clearVinError();
    this.clearVehicle();
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) { this.showVinError(t.vinLen); return; }
    var btn = this.el.vinDecodeBtn;
    btn.disabled = true;
    btn.textContent = t.checking;
    // only the newest decode, for the VIN still in the field, may set the vehicle
    var seq = this._dseq = (this._dseq || 0) + 1;
    var current = function () {
      return seq === self._dseq && String(self.el.vinInput.value || '').trim().toUpperCase() === vin;
    };
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 15000) : null;
    fetch(NHTSA_DECODE + encodeURIComponent(vin) + '?format=json', ctrl ? { signal: ctrl.signal } : undefined)
      .then(function (res) { if (!res.ok) throw new Error('NHTSA ' + res.status); return res.json(); })
      .then(function (data) {
        if (!current()) return;
        var r = (data && data.Results && data.Results[0]) || {};
        var get = function (k) { var v = r[k]; return !v || v === 'Not Applicable' || v === 'null' ? '' : String(v); };
        var make = get('Make').toUpperCase(), model = get('Model'), year = parseInt(get('ModelYear'), 10);
        if (!ALLOWED_MAKES[make]) { self.showVinError(make ? t.notOurBrand(make, model) : t.noDecode); return; }
        if (!model || !year) { self.showVinError(t.partialDecode); return; }
        if (self.minYear && year < self.minYear) { self.showVinError(t.tooOld(self.minYear)); return; }
        self.state.vehicle = {
          vin: vin, make: make, model: model, year: String(year),
          bodyClass: get('BodyClass'), fuelType: get('FuelTypePrimary'), trim: get('Trim')
        };
        self.renderVehicle();
        self.updateAnalyzeButton();
      })
      .catch(function (err) {
        if (window.console) console.warn('[dpa] VIN decode failed', err);
        if (current()) self.showVinError(t.decodeDown);
      })
      .then(function () {
        if (timer) clearTimeout(timer);
        if (seq !== self._dseq) return; // a newer decode owns the button
        btn.disabled = false;
        btn.textContent = t.decode;
      });
  };

  DamageAnalyzer.prototype.renderVehicle = function () {
    var v = this.state.vehicle, box = this.el.vehicleResult;
    if (!box) return;
    if (!v) { box.hidden = true; box.textContent = ''; return; }
    var meta = [v.bodyClass, v.fuelType].filter(Boolean).join(' · ');
    box.innerHTML =
      '<div class="dpa-vehicle-card">' +
      '<div class="dpa-vehicle-card__name">' + escapeHtml(v.make + ' ' + v.model + (v.trim ? ' ' + v.trim : '') + ' · ' + v.year) + '</div>' +
      (meta ? '<div class="dpa-vehicle-card__meta">' + escapeHtml(meta) + '</div>' : '') +
      '</div>';
    box.hidden = false;
  };

  DamageAnalyzer.prototype.clearVehicle = function () {
    this.state.vehicle = null;
    if (this.el.vehicleResult) { this.el.vehicleResult.hidden = true; this.el.vehicleResult.textContent = ''; }
    this.updateAnalyzeButton();
  };
  DamageAnalyzer.prototype.showVinError = function (msg) {
    if (!this.el.vinError) return;
    this.el.vinError.textContent = msg;
    this.el.vinError.hidden = false;
  };
  DamageAnalyzer.prototype.clearVinError = function () {
    if (this.el.vinError) { this.el.vinError.hidden = true; this.el.vinError.textContent = ''; }
  };

  // ─── Photos ─────────────────────────────────────────────
  DamageAnalyzer.prototype.addFiles = function (files) {
    var t = this.t, err = '';
    if (this.state.loading) return; // the running analysis owns the status box
    for (var i = 0; i < files.length; i++) {
      var file = files[i];
      if (this.state.images.length >= MAX_IMAGES) { err = t.maxImages(MAX_IMAGES); break; }
      var type = String(file.type || '').toLowerCase();
      if (!ACCEPTED.test(type) && !/\.(heic|heif)$/i.test(file.name || '')) { err = t.badFormat; continue; }
      if (file.size > MAX_PICK_BYTES) { err = t.tooBig; continue; }
      var entry = { file: file, url: URL.createObjectURL(file), checking: isHeic(file) };
      this.state.images.push(entry);
      if (entry.checking) this.checkHeic(entry);
    }
    // once, after the whole batch: a good file picked with a bad one no longer wipes the bad one's message
    this.setStatus(err, err ? 'error' : '');
    this.renderPreviews();
    this.updateAnalyzeButton();
  };

  /**
   * v2.3.2 (bug hunt DMG-A-02): a HEIC the browser cannot decode (Chrome, Edge, Firefox) used to go up as the
   * original, which the worker always refuses (a camera HEIC carries location/device data) — after the Turnstile
   * token was spent and megabytes uploaded, and again on every retry. It is refused here at once instead. One
   * check at a time (two 12 MP decodes at once can crash a phone tab); a browser that opens HEIC (Safari) keeps it.
   */
  DamageAnalyzer.prototype.checkHeic = function (entry) {
    var self = this;
    this._heicChecks = (this._heicChecks || Promise.resolve()).then(function () {
      if (self.state.images.indexOf(entry) === -1) return; // removed meanwhile
      return decodeImage(entry.file).then(function (src) {
        if (src && src.close) src.close();
        entry.checking = false;
      }, function () {
        var at = self.state.images.indexOf(entry);
        if (entry.url) URL.revokeObjectURL(entry.url);
        entry.checking = false;
        // v2.3.2 review: the customer removed it (×) during its own check — nothing to say about it
        if (at === -1) return;
        self.state.images.splice(at, 1);
        if (!self.state.loading) self.setStatus(self.t.heicNo, 'error');
      }).then(function () {
        self.renderPreviews();
        self.updateAnalyzeButton();
      });
    });
  };

  DamageAnalyzer.prototype.renderPreviews = function () {
    var c = this.el.previews, t = this.t;
    if (c) {
      if (!this.state.images.length) { c.hidden = true; c.textContent = ''; }
      else {
        c.hidden = false;
        c.innerHTML = this.state.images.map(function (img, i) {
          return '<div class="dpa-thumb">' +
            // a HEIC under check: a label, not a broken image (Chrome cannot show it)
            (img.checking
              ? '<span class="dpa-thumb__img dpa-thumb__img--heic" role="img" aria-label="' + escapeAttr(t.photo + ' ' + (i + 1)) + '">HEIC</span>'
              : '<img class="dpa-thumb__img" src="' + escapeAttr(img.url) + '" alt="' + escapeAttr(t.photo + ' ' + (i + 1)) + '" />') +
            '<button type="button" class="dpa-thumb__remove" data-dpa-remove="' + i + '" aria-label="' + escapeAttr(t.removePhoto) + '">×</button>' +
            '<span class="dpa-thumb__badge">' + escapeHtml(t.photo + ' ' + (i + 1)) + '</span>' +
            '</div>';
        }).join('');
      }
    }
    if (this.el.drop) this.el.drop.style.display = this.state.images.length >= MAX_IMAGES ? 'none' : '';
  };

  DamageAnalyzer.prototype.removeImage = function (index) {
    if (this.state.loading) return;
    var img = this.state.images[index];
    if (img && img.url) URL.revokeObjectURL(img.url);
    this.state.images.splice(index, 1);
    if (this.el.fileInput) this.el.fileInput.value = '';
    this.setStatus('');
    this.renderPreviews();
    this.updateAnalyzeButton();
  };

  DamageAnalyzer.prototype.updateAnalyzeButton = function () {
    if (!this.el.analyzeBtn) return;
    var confirmed = !this.el.confirm || this.el.confirm.checked;
    var checking = this.state.images.some(function (img) { return img.checking; }); // v2.3.2: a HEIC still under check
    var ready = this.state.images.length > 0 && !checking && !!this.state.vehicle && confirmed && !this.state.loading;
    this.el.analyzeBtn.disabled = !ready;
  };

  // ─── Turnstile ──────────────────────────────────────────
  DamageAnalyzer.prototype.currentToken = function () {
    if (window.turnstile && this.el.turnstile) {
      // the API is authoritative: it knows when a token expired or was spent
      try { return window.turnstile.getResponse(this.el.turnstile) || ''; } catch (e) { return ''; }
    }
    var input = this.root.querySelector('[name="cf-turnstile-response"]');
    return input && input.value ? input.value : '';
  };

  /**
   * Resolves with a token, '' when no widget is configured, null when the
   * check did not finish in time, or false when the Turnstile script never
   * loaded (ad blocker / network filter).
   */
  DamageAnalyzer.prototype.waitForToken = function () {
    var self = this;
    if (!this.el.turnstile) return Promise.resolve('');
    return new Promise(function (resolve) {
      var start = Date.now();
      (function poll() {
        var tk = self.currentToken();
        if (tk) return resolve(tk);
        if (Date.now() - start > TURNSTILE_WAIT_MS) return resolve(window.turnstile ? null : false);
        setTimeout(poll, 250);
      })();
    });
  };

  DamageAnalyzer.prototype.resetTurnstile = function () {
    try { if (window.turnstile && this.el.turnstile) window.turnstile.reset(this.el.turnstile); } catch (e) { /* noop */ }
  };

  // ─── Analyze ────────────────────────────────────────────
  DamageAnalyzer.prototype.analyze = function () {
    var self = this, t = this.t;
    if (this.state.loading) return;
    if (!this.workerUrl) { this.setStatus(this.workerUrlBad ? t.badWorker : t.noWorker, 'error'); return; }
    if (!this.state.images.length) { this.setStatus(t.needPhoto, 'error'); return; }
    if (!this.state.vehicle) { this.setStatus(t.needVin, 'error'); return; }
    if (this.el.confirm && !this.el.confirm.checked) { this.setStatus(t.confirmVehicle, 'error'); return; }

    // snapshot: editing the VIN or the photos while this runs cannot mix two cars
    var vehicle = this.state.vehicle;
    var files = this.state.images.map(function (img) { return img.file; });
    var tokenSpent = false;

    this.state.loading = true;
    this.updateAnalyzeButton();
    this.el.results.hidden = true;
    this.el.results.textContent = '';
    this._order = null;
    this.stopWaTimer();
    if (this.el.turnstile) this.setStatus(t.securityCheck);

    var finish = function () {
      if (self._progressTimer) { clearInterval(self._progressTimer); self._progressTimer = null; }
      self.state.loading = false;
      self.updateAnalyzeButton();
    };

    // photos one after another (two 12 MP decodes at once can crash a phone
    // tab), the Turnstile check in parallel
    var prepared = files.reduce(function (chain, file) {
      return chain.then(function (out) {
        return self.prepareImage(file).then(function (b) { out.push(b); return out; });
      });
    }, Promise.resolve([]));

    Promise.all([prepared, this.waitForToken()]).then(function (res) {
      // the check often finishes long before a phone has prepared the photos: take the token as it
      // is NOW (the widget renews an expired one), so a token that ran out meanwhile is never sent
      return (res[1] ? self.waitForToken() : Promise.resolve(res[1])).then(function (token) { return [res[0], token]; });
    }).then(function (res) {
      var blobs = res[0], token = res[1];
      // v2.3.2 (DMG-A-02): a HEIC that could not be re-encoded is never uploaded as the original (always refused)
      for (var hi = 0; hi < blobs.length; hi++) {
        if (blobs[hi] === files[hi] && isHeic(files[hi])) { self.setStatus(t.heicNo, 'error'); return; }
      }
      if (token === false) { self.setStatus(t.tsBlocked, 'error'); return; }
      if (token === null) { self.setStatus(t.waitCheck, 'error'); return; }
      var total = 0;
      for (var i = 0; i < blobs.length; i++) {
        if (blobs[i].size > MAX_UPLOAD_BYTES) { self.setStatus(t.tooBig, 'error'); return; }
        total += blobs[i].size;
      }
      if (total > MAX_UPLOAD_TOTAL) { self.setStatus(t.tooBigTotal, 'error'); return; }

      self.startProgress(vehicle);
      var form = new FormData();
      blobs.forEach(function (b, i) { form.append('image', b, 'photo-' + (i + 1) + (b.type === 'image/jpeg' ? '.jpg' : '')); });
      form.append('vin', vehicle.vin);
      form.append('lang', self.lang);
      if (token) { form.append('cf-turnstile-response', token); tokenSpent = true; }

      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, REQUEST_TIMEOUT_MS) : null;
      // the token also rides in the query: worker 2.3+ checks it BEFORE reading the upload, so a bot
      // cannot make it buffer megabytes (the form copy above is for an older worker)
      var url = self.workerUrl + (self.workerUrl.indexOf('?') === -1 ? '?' : '&') + 'lang=' + encodeURIComponent(self.lang) +
        (token ? '&ts=' + encodeURIComponent(token) : '');
      return fetch(url, { method: 'POST', body: form, credentials: 'omit', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) {
          return r.json().catch(function () { return null; }).then(function (data) { return { res: r, data: data }; });
        })
        .then(function (x) {
          if (timer) clearTimeout(timer);
          var res = x.res, data = x.data;
          if (!res.ok || !data || data.ok === false) {
            var msg = (data && (data.message || data.error)) || t.httpError(res.status);
            var code = data && data.code;
            var ra = parseInt(res.headers.get('Retry-After') || '', 10);
            if ((!code || RETRY_HINT_CODES[code]) && (res.status === 429 || res.status === 503) && ra > 0 && ra <= 300) msg += t.retryIn(ra);
            self.setStatus(msg, 'error');
            return;
          }
          self.stopProgress(true);
          self.renderResults(data, vehicle);
          self.setStatus('');
          setTimeout(function () {
            // v2.3.2 (DMG-A-11): no smooth scrolling for a visitor who asked for reduced motion
            if (self.el.results.scrollIntoView) self.el.results.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
          }, 100);
        }, function (err) {
          if (timer) clearTimeout(timer);
          if (window.console) console.warn('[dpa] analyze failed', err);
          self.setStatus(err && err.name === 'AbortError' ? t.timeout : t.netFail, 'error');
        });
    }).catch(function (err) {
      if (window.console) console.warn('[dpa] prepare failed', err);
      self.setStatus(t.netFail, 'error');
    }).then(function () {
      if (tokenSpent) self.resetTurnstile(); // tokens are single-use; an unspent one stays valid
      finish();
    });
  };

  // ─── Status + progress ──────────────────────────────────
  DamageAnalyzer.prototype.setStatus = function (text, type) {
    if (!this.el.status) return;
    if (this._progressTimer && type === 'error') { clearInterval(this._progressTimer); this._progressTimer = null; }
    this.el.status.textContent = text || '';
    this.el.status.className = 'dpa-status' + (type === 'error' ? ' dpa-status--error' : '');
  };

  DamageAnalyzer.prototype.startProgress = function (vehicle) {
    var self = this, t = this.t;
    var stages = [
      { at: 0, label: t.st1, log: t.st1log },
      { at: 22, label: t.st2, log: t.st2log },
      { at: 78, label: t.st3, log: t.st3log }
    ];
    this._progress = 0;
    this._loggedAt = {};
    this.el.status.className = 'dpa-status';
    // the status box is the live region; only the stage label is announced, not every percent
    this.el.status.innerHTML =
      '<div class="dpa-progress">' +
      '<div class="dpa-progress__head"><span class="dpa-progress__pct" aria-hidden="true">0%</span><span class="dpa-progress__stage"></span></div>' +
      '<div class="dpa-progress__bar" aria-hidden="true"><div class="dpa-progress__fill"></div></div>' +
      '<pre class="dpa-progress__log" aria-hidden="true"></pre>' +
      '</div>';
    var fill = this.el.status.querySelector('.dpa-progress__fill');
    // v2.3.2 (DMG-A-11): full width once, then transform: scaleX() — `width` from JS meant layout 8× a second
    fill.style.width = '100%';
    fill.style.transformOrigin = '0 50%';
    var pctEl = this.el.status.querySelector('.dpa-progress__pct');
    var stEl = this.el.status.querySelector('.dpa-progress__stage');
    var logEl = this.el.status.querySelector('.dpa-progress__log');
    var addLog = function (txt) {
      var line = document.createElement('div');
      line.className = 'dpa-progress__line';
      line.textContent = txt;
      logEl.appendChild(line);
      logEl.scrollTop = logEl.scrollHeight;
    };
    addLog('> VIN ' + ((vehicle && vehicle.vin) || '') + ' ✓');
    var tick = function () {
      if (self._progress < 90) self._progress += Math.max(0.4, (90 - self._progress) * 0.045);
      fill.style.transform = 'scaleX(' + (self._progress / 100).toFixed(3) + ')';
      pctEl.textContent = Math.round(self._progress) + '%';
      var s = stages.filter(function (x) { return self._progress >= x.at; }).pop();
      if (s) {
        // touch the live region only when the stage really changes
        if (stEl.textContent !== s.label) stEl.textContent = s.label;
        if (!self._loggedAt[s.at]) { self._loggedAt[s.at] = true; addLog(s.log); }
      }
    };
    tick();
    this._progressTimer = setInterval(tick, 120);
  };

  DamageAnalyzer.prototype.stopProgress = function (success) {
    if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
    if (!success) return;
    var q = this.el.status.querySelector.bind(this.el.status);
    var fill = q('.dpa-progress__fill'), pctEl = q('.dpa-progress__pct'), stEl = q('.dpa-progress__stage'), logEl = q('.dpa-progress__log');
    if (fill) fill.style.transform = 'scaleX(1)';
    if (pctEl) pctEl.textContent = '100%';
    if (stEl) stEl.textContent = this.t.done;
    if (logEl) {
      var line = document.createElement('div');
      line.className = 'dpa-progress__line dpa-progress__line--done';
      line.textContent = this.t.doneLog;
      logEl.appendChild(line);
    }
  };

  // ─── Results ────────────────────────────────────────────
  DamageAnalyzer.prototype.renderResults = function (data, vehicle) {
    var t = this.t, parts = [];
    this._shownVehicle = vehicle || this.state.vehicle; // catalog-search links use the analysed car
    // order state for this result: distinct products, their damage groups, the customer's picks
    this._order = {
      items: [], byId: {}, sel: {}, groups: [], data: data, vehicle: (data.vehicle && data.vehicle.vin ? data.vehicle : this._shownVehicle) || {},
      // the worker's stored offer (v2.2.0+): one short link carries the whole order
      offerId: OFFER_ID.test(String(data.offer_id || '')) ? data.offer_id : '',
      offerCode: OFFER_CODE.test(String(data.offer_code || '')) ? data.offer_code : ''
    };
    this.stopWaTimer();
    var sevClass = SEVERITY_CLASS[data.severity_level] || data.severity || 'საშუალო';
    var sevText = data.severity_label || data.severity || '';
    parts.push(
      '<div class="dpa-assessment">' +
      '<div class="dpa-assessment__severity dpa-assessment__severity--' + escapeAttr(sevClass) + '">' + escapeHtml(t.severity + ' — ' + sevText) + '</div>' +
      (data.overall_assessment ? '<p class="dpa-assessment__text">' + escapeHtml(data.overall_assessment) + '</p>' : '') +
      (data.impact_area ? '<div class="dpa-assessment__impact">' + escapeHtml(t.impact + ': ' + data.impact_area) + '</div>' : '') +
      '</div>'
    );
    var notes = Array.isArray(data.notes) ? data.notes : [];
    for (var n = 0; n < notes.length; n++) parts.push(this.note(notes[n]));

    var visible = Array.isArray(data.visible_damage) ? data.visible_damage : [];
    if (visible.length) {
      parts.push('<h2 class="dpa-results__heading">' + escapeHtml(t.visible) + ' <span class="dpa-results__heading-count">' + visible.length + '</span></h2>');
      for (var i = 0; i < visible.length; i++) parts.push(this.renderDamageCard(visible[i]));
    }
    var hidden = Array.isArray(data.likely_internal_damage) ? data.likely_internal_damage : [];
    if (hidden.length) {
      parts.push('<h2 class="dpa-results__heading">' + escapeHtml(t.hidden) + ' <span class="dpa-results__heading-count">' + hidden.length + '</span></h2>');
      for (var j = 0; j < hidden.length; j++) parts.push(this.renderInternalCard(hidden[j]));
    }
    if (this._order.groups.length) {
      this.applyDefaultPicks();
      parts.push(this.renderOrderPanel());
    }
    this.el.results.innerHTML = parts.join('');
    this.el.results.hidden = false;
    this.syncOrder();
  };

  DamageAnalyzer.prototype.note = function (text) {
    return '<div class="dpa-damage__side-note" role="note"><span class="dpa-damage__side-note-icon" aria-hidden="true">ⓘ</span><span>' + escapeHtml(text) + '</span></div>';
  };

  DamageAnalyzer.prototype.renderDamageCard = function (dmg) {
    var t = this.t, self = this;
    var g = this.beginGroup(dmg);
    var products = (dmg.products || []).map(function (p) { return self.renderProductCard(p, g); }).join('');
    var block;
    if (products) block = '<div class="dpa-products">' + products + '</div>';
    else if (dmg.used_original_only && dmg.note) block = '';
    else block = '<div class="dpa-products__empty">' + escapeHtml(t.noProduct) + ' ' + this.renderSearchFallback(dmg.part) + '</div>';
    var notes = '';
    if (dmg.side_uncertain) notes += this.warn(t.sideNote);
    if (dmg.position_uncertain) notes += this.warn(t.posNote);
    if (dmg.used_original_only && dmg.note) notes += this.warn(dmg.note);
    var confClass = CONF_CLASS[dmg.confidence_level] || dmg.confidence || '';
    var confText = dmg.confidence_label || dmg.confidence || '';
    return '<div class="dpa-damage">' +
      '<div class="dpa-damage__head"><div class="dpa-damage__body">' +
      '<h3 class="dpa-damage__name">' + escapeHtml(dmg.part) + '</h3>' +
      '<p class="dpa-damage__desc">' + escapeHtml(dmg.description) + '</p>' +
      '</div><span class="dpa-damage__conf dpa-damage__conf--' + escapeAttr(confClass) + '">' + escapeHtml(confText) + '</span></div>' +
      notes + block + '</div>';
  };

  DamageAnalyzer.prototype.warn = function (text) {
    return '<div class="dpa-damage__side-note" role="note"><span class="dpa-damage__side-note-icon" aria-hidden="true">⚠</span><span>' + escapeHtml(text) + '</span></div>';
  };

  DamageAnalyzer.prototype.renderInternalCard = function (dmg) {
    var self = this;
    var g = this.beginGroup(dmg, true);
    var products = (dmg.products || []).map(function (p) { return self.renderProductCard(p, g); }).join('');
    var usedOnly = dmg.used_original_only && dmg.note;
    return '<div class="dpa-internal">' +
      '<h3 class="dpa-internal__name">' + escapeHtml(dmg.part) + '</h3>' +
      '<p class="dpa-internal__reason">' + escapeHtml(dmg.reason) + '</p>' +
      (usedOnly ? this.warn(dmg.note) : '') +
      (products ? '<div class="dpa-products" style="padding: 0; margin-top: 8px;">' + products + '</div>' : (usedOnly ? '' : this.renderSearchFallback(dmg.part))) +
      '</div>';
  };

  DamageAnalyzer.prototype.productHref = function (p) {
    // current-locale link from the handle; the worker's absolute url is the fallback
    if (p && typeof p.handle === 'string' && /^[^\/?#\s]{1,255}$/.test(p.handle)) {
      return this.rootUrl + '/products/' + encodeURIComponent(p.handle);
    }
    return safeUrl(p && p.url) || '#';
  };

  DamageAnalyzer.prototype.renderProductCard = function (p, g) {
    var t = this.t;
    var img = safeUrl(p.imageUrl);
    var imgHtml = img
      ? '<img class="dpa-product__img" src="' + escapeAttr(img) + '" alt="' + escapeAttr(p.imageAlt || p.title) + '" loading="lazy" decoding="async" />'
      : '<div class="dpa-product__img dpa-product__img--missing">' + escapeHtml(t.noImage) + '</div>';
    var card = '<a class="dpa-product" href="' + escapeAttr(this.productHref(p)) + '" target="_blank" rel="noopener">' +
      imgHtml +
      '<div class="dpa-product__info">' +
      '<p class="dpa-product__title">' + escapeHtml(p.title) + '</p>' +
      (p.vendor ? '<span class="dpa-product__vendor">' + escapeHtml(p.vendor) + '</span>' : '') +
      '<span class="dpa-product__price">' + escapeHtml(this.formatPrice(p.price)) + '</span>' +
      (p.availableForSale === false ? '<span class="dpa-product__unavailable">' + escapeHtml(t.soldOut) + '</span>' : '') +
      '</div></a>';
    if (!this._order) return card;
    // the checkbox sits beside the link (an input inside <a> is invalid HTML), overlaid on the photo corner
    var key = this.registerProduct(p, g);
    return '<div class="dpa-pick" data-dpa-pick="' + key + '">' +
      '<label class="dpa-pick__box"><input type="checkbox" class="dpa-pick__input" data-dpa-pick-input="' + key + '"' +
      ' aria-label="' + escapeAttr(t.pick + ': ' + (p.title || '')) + '"><span class="dpa-pick__mark" aria-hidden="true"></span></label>' +
      card + '</div>';
  };

  // ─── Order: pick products → total → WhatsApp ────────────
  // Every product card gets a checkbox. Picks are kept per distinct product
  // (the same product shown under two damages is ordered once). Default:
  // per damaged part its best in-stock match (the worker ranks them), or
  // every in-stock product when the section setting says "all" — alternatives
  // of one part (another brand, a piece of the panel) are not summed by default.
  // A part whose side (or front/rear) the photo did not settle is never
  // pre-picked: its list mixes both sides and the customer must choose.
  DamageAnalyzer.prototype.beginGroup = function (dmg, hidden) {
    var g = {
      part: String((dmg && dmg.part) || ''), keys: [], hidden: !!hidden,
      used: !!(dmg && dmg.used_original_only),
      uncertain: !!(dmg && (dmg.side_uncertain || dmg.position_uncertain))
    };
    if (this._order) this._order.groups.push(g);
    return g;
  };

  DamageAnalyzer.prototype.registerProduct = function (p, g) {
    var o = this._order;
    // products without an id/handle (old responses) are never merged with each other
    var id = p && (p.id || p.handle) ? String(p.id || p.handle) : '#' + o.items.length;
    var key = Object.prototype.hasOwnProperty.call(o.byId, id) ? o.byId[id] : undefined;
    if (key === undefined) {
      key = String(o.items.length);
      o.byId[id] = key;
      var ix = p && typeof p.ix === 'number' && p.ix >= 0 && p.ix <= MAX_OFFER_IX && p.ix % 1 === 0 ? p.ix : -1;
      o.items.push({ key: key, p: p, part: g ? g.part : '', visible: false, ix: ix });
    }
    var it = o.items[+key];
    if (g && !g.hidden) it.visible = true; // "likely hidden" only when no visible damage shows it
    if (g && g.keys.indexOf(key) === -1) g.keys.push(key);
    return key;
  };

  DamageAnalyzer.prototype.applyDefaultPicks = function () {
    var o = this._order, all = this.orderDefault === 'all';
    for (var i = 0; i < o.groups.length; i++) {
      if (o.groups[i].uncertain) continue;
      var keys = o.groups[i].keys;
      for (var j = 0; j < keys.length; j++) {
        var it = o.items[+keys[j]];
        if (!it || it.p.availableForSale === false) continue;
        o.sel[it.key] = true;
        if (!all) break;
      }
    }
  };

  DamageAnalyzer.prototype.renderOrderPanel = function () {
    var t = this.t, o = this._order;
    // flat children: the section CSS lays them out as a grid (compact two-row bar on phones)
    return '<div class="dpa-order" data-dpa-order>' +
      '<div class="dpa-order__sum"><span class="dpa-order__title">' + escapeHtml(t.orderTitle) + '</span>' +
      '<span class="dpa-order__count" data-dpa-order-count></span></div>' +
      '<div class="dpa-order__total" aria-live="polite"><span class="dpa-order__total-label">' + escapeHtml(t.total) + '</span> <span class="dpa-order__total-value" data-dpa-order-total></span></div>' +
      '<p class="dpa-order__tbd" data-dpa-order-tbd hidden>' + escapeHtml(t.priceTbd) + '</p>' +
      (o.items.length
        ? '<div class="dpa-order__tools"><button type="button" class="dpa-order__tool" data-dpa-order-all>' + escapeHtml(t.selectAll) + '</button>' +
          '<button type="button" class="dpa-order__tool" data-dpa-order-none>' + escapeHtml(t.clearAll) + '</button></div>'
        : '') +
      (this.waNumber
        ? '<a class="dpa-order__wa" data-dpa-order-wa href="#" target="_blank" rel="noopener">' +
          // v2.3.2 (DMG-A-09): the shine's own clipped layer — it moves with transform (section CSS)
          '<span class="dpa-order__wa-glare" aria-hidden="true"></span>' +
          '<span class="dpa-order__wa-icon" aria-hidden="true">' + this.waIconHtml() + '</span>' +
          '<span class="dpa-order__wa-text"><span class="dpa-order__wa-label" data-dpa-order-wa-label></span>' +
          '<span class="dpa-order__wa-sub" data-dpa-order-wa-sub>' + escapeHtml(waPretty(this.waNumber)) + '</span></span>' +
          '<span class="dpa-order__wa-arrow" aria-hidden="true">→</span></a>' +
          '<p class="dpa-order__hint" data-dpa-order-hint>' + escapeHtml(t.waHint) + '</p>'
        : '') +
      '</div>';
  };

  /**
   * The button icon: the image the owner uploaded in the section settings
   * (e.g. the official icon from WhatsApp's brand resources), otherwise a
   * neutral "send" glyph.
   */
  DamageAnalyzer.prototype.waIconHtml = function () {
    if (this.waIcon) return '<img src="' + escapeAttr(this.waIcon) + '" alt="" width="26" height="26" loading="lazy" decoding="async">';
    return '<svg viewBox="0 0 24 24" width="22" height="22" focusable="false">' +
      '<path fill="currentColor" d="M3.6 10.4 19.2 3.9c1.1-.5 2.2.6 1.8 1.7l-6.2 15.7c-.5 1.2-2.2 1.1-2.5-.1l-1.6-5.7a1 1 0 0 0-.7-.7L4.3 13.2c-1.2-.4-1.4-2.1-.7-2.8Z"/>' +
      '<path fill="none" stroke="rgba(0,0,0,.28)" stroke-width="1.4" stroke-linecap="round" d="m10.4 13.6 4.9-4.9"/></svg>';
  };

  DamageAnalyzer.prototype.setPicked = function (key, on) {
    if (!this._order || !this._order.items[+key]) return;
    this._order.sel[key] = !!on;
    this.syncOrder();
  };

  /** "Select all" takes every in-stock product shown; "clear" empties the order. */
  DamageAnalyzer.prototype.pickAll = function (on) {
    var o = this._order;
    for (var i = 0; i < o.items.length; i++) {
      var it = o.items[i];
      o.sel[it.key] = !!on && it.p.availableForSale !== false;
    }
    this.syncOrder();
  };

  DamageAnalyzer.prototype.orderSum = function () {
    var o = this._order, total = 0, count = 0, tbd = false, currency = '';
    for (var i = 0; i < o.items.length; i++) {
      var it = o.items[i];
      if (!o.sel[it.key]) continue;
      count++;
      var amount = it.p && it.p.price ? parseFloat(it.p.price.amount) : NaN;
      if (isFinite(amount) && amount > 0) {
        total += amount;
        if (!currency) currency = it.p.price.currencyCode || '';
      } else tbd = true;
    }
    return { count: count, total: Math.round(total * 100) / 100, currency: currency || this.currency, tbd: tbd };
  };

  /** Re-applies the picks to every card and refreshes the total and the WhatsApp link. */
  DamageAnalyzer.prototype.syncOrder = function () {
    var o = this._order, t = this.t, box = this.el.results;
    if (!o || !box) return;
    var cards = box.querySelectorAll('[data-dpa-pick]');
    for (var i = 0; i < cards.length; i++) {
      var on = !!o.sel[cards[i].getAttribute('data-dpa-pick')];
      if (on) cards[i].classList.add('is-picked'); else cards[i].classList.remove('is-picked');
      var input = cards[i].querySelector('[data-dpa-pick-input]');
      if (input) input.checked = on;
    }
    var sum = this.orderSum();
    var q = function (sel) { return box.querySelector(sel); };
    var countEl = q('[data-dpa-order-count]'), totalEl = q('[data-dpa-order-total]'), tbdEl = q('[data-dpa-order-tbd]');
    if (countEl) countEl.textContent = t.selectedN(sum.count);
    if (totalEl) {
      var totalText = this.formatPrice({ amount: String(sum.total), currencyCode: sum.currency });
      if (totalEl.textContent && totalEl.textContent !== totalText) {
        // restart the little "bump" so every change of the sum is noticed
        totalEl.classList.remove('is-bump');
        void totalEl.offsetWidth;
        totalEl.classList.add('is-bump');
      }
      totalEl.textContent = totalText;
    }
    if (tbdEl) tbdEl.hidden = !(sum.count && sum.tbd);
    this.paintWa();
  };

  /**
   * The order link: this page (Georgian root, so the operator reads it in
   * Georgian) + the worker's offer id + a hex bitmask of the picked product
   * indexes — short, ASCII-only, and nothing in it can be forged into other
   * products: the page shows only what the worker stored for that analysis.
   */
  DamageAnalyzer.prototype.orderLink = function () {
    var o = this._order;
    if (!o || !o.offerId) return '';
    var bits = [];
    for (var i = 0; i < o.items.length; i++) {
      if (o.sel[o.items[i].key] && o.items[i].ix >= 0) bits.push(o.items[i].ix);
    }
    var base = this.pageBase();
    return base ? base + 'order=' + o.offerId + '&p=' + toMask(bits) : '';
  };

  /** "https://partpoint.ge/pages/x?" (a theme preview keeps its preview_theme_id, so a duplicate theme can be tested end to end). */
  DamageAnalyzer.prototype.pageBase = function () {
    var loc = window.location || {}, path = String(loc.pathname || '/');
    if (this.rootUrl && (path === this.rootUrl || path.indexOf(this.rootUrl + '/') === 0)) path = path.slice(this.rootUrl.length) || '/';
    var origin = /^https?:$/.test(loc.protocol || '') && loc.host ? loc.protocol + '//' + loc.host : safeUrl(this.shopUrl).replace(/\/+$/, '');
    if (!/^https?:\/\//i.test(origin)) return '';
    var preview = /[?&]preview_theme_id=(\d{1,20})(?:&|$)/.exec(String(loc.search || ''));
    return origin + path + '?' + (preview ? 'preview_theme_id=' + preview[1] + '&' : '');
  };

  /**
   * The WhatsApp text: labels in the page language, product titles in the
   * store's Georgian (what the operator searches by), prices, the total, every
   * damaged part that has no picked product (and why), and ONE link to the
   * order page under the order code. `detail` shrinks the text when the wa.me link
   * would get too long for a phone (Georgian costs 9 URL characters per letter):
   *   3 = full · 2 = titles ≤ 40, no vendor · 1 = part + price only ·
   *   0 = car, count, total and the link (which carries every item).
   */
  DamageAnalyzer.prototype.orderMessage = function (detail) {
    var t = this.t, o = this._order, v = o.vehicle || {}, d = o.data || {};
    var sum = this.orderSum(), lines = [];
    lines.push(sum.count ? t.mHello : t.mConsult, '');
    var car = [v.make, v.model, v.year].filter(Boolean).join(' ');
    if (car) lines.push(t.mCar + ': ' + car);
    if (v.vin) lines.push('VIN: ' + v.vin);
    var sev = [d.severity_label || d.severity, d.impact_area].filter(Boolean).join(' · ');
    if (sev && detail > 0) lines.push(t.mSeverity + ': ' + sev);
    if (sum.count) {
      lines.push('', t.mItems + ' (' + sum.count + ')' + (detail > 0 ? ':' : ''));
      var n = 0;
      for (var i = 0; i < o.items.length && detail > 0; i++) {
        var it = o.items[i];
        if (!o.sel[it.key]) continue;
        n++;
        var amount = it.p.price ? parseFloat(it.p.price.amount) : NaN;
        var info = [isFinite(amount) && amount > 0 ? this.formatPrice(it.p.price) : t.mTbd];
        if (it.p.vendor && detail > 2) info.push(String(it.p.vendor));
        if (it.p.availableForSale === false) info.push(t.mSoldOut);
        lines.push(n + '. ' + it.part + (it.visible ? '' : ' (' + t.mLikely + ')'));
        if (detail > 1) lines.push('   ' + clip(String(it.p.title_ka || it.p.title || ''), detail > 2 ? 200 : 40));
        lines.push('   ' + info.join(' · '));
      }
      if (detail > 0) lines.push('');
      lines.push(t.mTotal + ': ' + this.formatPrice({ amount: String(sum.total), currencyCode: sum.currency }) + (sum.tbd ? ' + ' + t.mTbd : ''));
    }
    // every damaged part without a picked product, so the operator sees the whole repair
    if (detail > 0) {
      var groups = [];
      for (var g = 0; g < o.groups.length; g++) {
        var grp = o.groups[g], picked = false, inStock = false;
        for (var k = 0; k < grp.keys.length; k++) {
          if (o.sel[grp.keys[k]]) picked = true;
          if (o.items[+grp.keys[k]].p.availableForSale !== false) inStock = true;
        }
        groups.push({ part: grp.part, hidden: grp.hidden, used: grp.used, uncertain: grp.uncertain, picked: picked, count: grp.keys.length, inStock: inStock });
      }
      var missing = missingParts(t, groups);
      if (missing.length) {
        lines.push('', t.mNoProduct + ':');
        for (var m = 0; m < missing.length; m++) lines.push('- ' + missing[m].part + ' — ' + missing[m].why);
      }
    }
    // last: "შეკვეთა PP-7K3Q9A:" + the link (a short label — every Georgian letter costs 9 characters of the wa.me link)
    var link = this.orderLink();
    if (link) lines.push('', t.mOrder + (o.offerCode ? ' ' + o.offerCode : '') + ':', link);
    return lines.join('\n');
  };

  DamageAnalyzer.prototype.waHref = function () {
    var base = 'https://wa.me/' + this.waNumber + '?text=';
    for (var detail = 3; detail >= 0; detail--) {
      try {
        var href = base + encodeURIComponent(this.orderMessage(detail));
        if (href.length <= WA_MAX_URL || detail === 0) return href;
      } catch (e) { /* a broken character in the data — try the next, smaller text */ }
    }
    return base + encodeURIComponent(this.t.mConsult);
  };

  // ─── WhatsApp spam guard ────────────────────────────────
  // The button only exists after an analysis that passed Turnstile and the
  // worker's daily limits (2 per client). On top of that one browser sends an
  // order at most WA_MAX_PER_ORDER times and never twice within WA_COOLDOWN_S;
  // a blocked click does nothing but show the countdown. (Anyone can still
  // write to a public number by hand — which is why every genuine order
  // carries a code and a link to a worker-verified order page.)
  DamageAnalyzer.prototype.waKey = function () {
    var o = this._order || {};
    return o.offerId || 'vin:' + String((o.vehicle && o.vehicle.vin) || '');
  };

  DamageAnalyzer.prototype.onWaClick = function (e) {
    var key = this.waKey(), st = waState(key), self = this;
    if (st.full || st.wait > 0) {
      if (e && e.preventDefault) e.preventDefault();
      this.paintWa();
      return;
    }
    waRecord(key);
    // repaint after the browser has taken the (unchanged) link
    setTimeout(function () { self.paintWa(); }, 0);
  };

  /** Link, label, sub-line and hint of the WhatsApp button for the current picks and send state. */
  DamageAnalyzer.prototype.paintWa = function () {
    var box = this.el.results, o = this._order, t = this.t;
    var wa = box && o && this.waNumber ? box.querySelector('[data-dpa-order-wa]') : null;
    if (!wa) return;
    var label = box.querySelector('[data-dpa-order-wa-label]'), sub = box.querySelector('[data-dpa-order-wa-sub]'), hint = box.querySelector('[data-dpa-order-hint]');
    var st = waState(this.waKey()), blocked = st.full || st.wait > 0;
    wa.setAttribute('href', this.waHref());
    if (blocked) { wa.setAttribute('aria-disabled', 'true'); wa.classList.add('is-sent'); }
    else { wa.removeAttribute('aria-disabled'); wa.classList.remove('is-sent'); }
    if (label) label.textContent = blocked ? t.waSent : (this.orderSum().count ? t.waOrder : t.waConsult);
    if (sub) sub.textContent = st.full ? t.waDone : st.wait > 0 ? t.waAgain(st.wait) : waPretty(this.waNumber);
    if (hint) hint.textContent = st.full ? t.waLimit : t.waHint;
    if (st.wait > 0 && !st.full) this.startWaTimer(); else this.stopWaTimer();
  };

  DamageAnalyzer.prototype.startWaTimer = function () {
    var self = this;
    if (this._waTimer) return;
    this._waTimer = setInterval(function () { self._waTimer && self.paintWa(); }, 1000);
  };

  DamageAnalyzer.prototype.stopWaTimer = function () {
    if (this._waTimer) { clearInterval(this._waTimer); this._waTimer = null; }
  };

  // ─── Order view (?order=<offer id>&p=<picks>) ──────────
  // What the operator opens from the WhatsApp message: the worker's stored
  // offer, the picked products with photo, live price/stock and link, the
  // total and the parts without a product. The analyzer form is hidden.
  DamageAnalyzer.prototype.initOfferView = function () {
    var search = String((window.location && window.location.search) || '');
    var m = /[?&]order=([A-Za-z0-9_-]{22})(?:&|$)/.exec(search);
    if (!m || !this.el.results) return false;
    var pm = /[?&]p=([0-9a-fA-F]{1,128})(?:&|$)/.exec(search);
    var self = this, t = this.t, box = this.el.results;
    this.root.classList.add('dpa-tool--offer');
    box.hidden = false;
    box.innerHTML = '<div class="dpa-offer" data-dpa-offer><p class="dpa-offer__status" role="status"><span class="dpa-offer__spin" aria-hidden="true"></span>' + escapeHtml(t.ofLoading) + '</p></div>';
    var url = this.offerUrl(m[1]);
    if (!url || typeof fetch !== 'function') { this.offerFailed(t.ofError); return true; }
    fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.json().catch(function () { return null; }).then(function (d) { return { r: r, d: d }; }); })
      .then(function (x) {
        if (x.r.status === 404) return self.offerFailed(t.ofNotFound);
        if (!x.r.ok || !x.d || !x.d.offer || typeof x.d.offer !== 'object') return self.offerFailed(t.ofError);
        self.renderOffer(x.d.offer, pm ? fromMask(pm[1]) : []);
      }, function () { self.offerFailed(t.ofError); });
    return true;
  };

  /** ".../analyze" → ".../offer/<id>" on the same worker. */
  DamageAnalyzer.prototype.offerUrl = function (id) {
    var base = String(this.workerUrl || '').replace(/[?#].*$/, '').replace(/\/analyze\/?$/, '').replace(/\/+$/, '');
    return /^https:\/\//i.test(base) && OFFER_ID.test(id) ? base + '/offer/' + id : '';
  };

  DamageAnalyzer.prototype.newAnalysisHref = function () {
    return String((window.location && window.location.pathname) || '/');
  };

  DamageAnalyzer.prototype.offerFailed = function (text) {
    this.el.results.innerHTML = '<div class="dpa-offer dpa-offer--error" data-dpa-offer>' +
      '<p class="dpa-offer__status" role="alert">' + escapeHtml(text) + '</p>' +
      '<a class="dpa-btn dpa-btn--outline dpa-offer__new" href="' + escapeAttr(this.newAnalysisHref()) + '">' + escapeHtml(this.t.ofNew) + '</a></div>';
  };

  DamageAnalyzer.prototype.renderOffer = function (of, picks) {
    var t = this.t, self = this;
    var products = Array.isArray(of.products) ? of.products : [];
    var groups = (Array.isArray(of.groups) ? of.groups : []).filter(function (g) { return g && typeof g.part === 'string' && Array.isArray(g.p); });
    var isPicked = {};
    for (var i = 0; i < picks.length; i++) if (products[picks[i]]) isPicked[picks[i]] = true;
    // each picked product once, under its first visible damage (a hidden one only when no visible damage shows it)
    var ordered = groups.filter(function (g) { return !g.hidden; }).concat(groups.filter(function (g) { return g.hidden; }));
    var rows = [], shown = {};
    for (var gi = 0; gi < ordered.length; gi++) {
      for (var pi = 0; pi < ordered[gi].p.length; pi++) {
        var ix = ordered[gi].p[pi];
        if (!isPicked[ix] || shown[ix]) continue;
        shown[ix] = true;
        rows.push({ ix: ix, p: products[ix], part: ordered[gi].part, hidden: !!ordered[gi].hidden });
      }
    }
    this._offerView = { rows: rows, live: {} };

    var car = of.car || {};
    var created = new Date(String(of.created || ''));
    var meta = [[car.make, car.model, car.year].filter(Boolean).join(' '), isNaN(created.getTime()) ? '' : fmtDate(created)].filter(Boolean).join(' · ');
    var dmg = [of.severity, of.zone].filter(Boolean).join(' · ');
    var html = '<div class="dpa-offer" data-dpa-offer>' +
      '<div class="dpa-offer__head">' +
      '<span class="dpa-offer__badge"><span aria-hidden="true">✓</span> ' + escapeHtml(t.ofVerified) + '</span>' +
      '<h2 class="dpa-offer__title">' + escapeHtml(t.ofTitle) + (OFFER_CODE.test(String(of.code || '')) ? ' <span class="dpa-offer__code">' + escapeHtml(of.code) + '</span>' : '') + '</h2>' +
      (meta ? '<p class="dpa-offer__meta">' + escapeHtml(meta) + '</p>' : '') +
      (dmg ? '<p class="dpa-offer__meta">' + escapeHtml(t.mSeverity + ': ' + dmg) + '</p>' : '') +
      '</div>';
    if (rows.length) {
      html += '<h3 class="dpa-offer__heading">' + escapeHtml(t.mItems) + ' <span class="dpa-results__heading-count">' + rows.length + '</span></h3><ol class="dpa-offer__list">';
      for (var r = 0; r < rows.length; r++) html += this.offerRow(rows[r], r + 1);
      html += '</ol><div class="dpa-offer__sum"><span class="dpa-offer__sum-label">' + escapeHtml(t.mTotal) + '</span>' +
        '<span class="dpa-offer__sum-value" data-dpa-offer-total></span></div>' +
        '<p class="dpa-offer__tbd" data-dpa-offer-tbd hidden>' + escapeHtml(t.priceTbd) + '</p>';
    } else {
      html += '<p class="dpa-offer__empty">' + escapeHtml(t.ofNone) + '</p>';
    }
    var gs = [];
    for (var g = 0; g < groups.length; g++) {
      var grp = groups[g], picked = false, inStock = false;
      for (var k = 0; k < grp.p.length; k++) {
        if (isPicked[grp.p[k]]) picked = true;
        if (products[grp.p[k]] && products[grp.p[k]].available !== false) inStock = true;
      }
      gs.push({ part: grp.part, hidden: !!grp.hidden, used: !!grp.used, uncertain: !!grp.uncertain, picked: picked, count: grp.p.length, inStock: inStock });
    }
    var missing = missingParts(t, gs);
    if (missing.length) {
      html += '<div class="dpa-offer__missing"><h3 class="dpa-offer__heading">' + escapeHtml(t.mNoProduct) + '</h3><ul>';
      for (var mi = 0; mi < missing.length; mi++) html += '<li><span class="dpa-offer__missing-part">' + escapeHtml(missing[mi].part) + '</span> — ' + escapeHtml(missing[mi].why) + '</li>';
      html += '</ul></div>';
    }
    var optRows = [];
    if (!rows.length) {
      // nothing picked (a consultation): every option the analysis offered, for the conversation
      var opts = '';
      for (var og = 0; og < groups.length; og++) {
        for (var op = 0; op < groups[og].p.length; op++) {
          var oix = groups[og].p[op];
          if (!products[oix]) continue;
          var orow = { ix: oix, p: products[oix], part: groups[og].part, hidden: !!groups[og].hidden };
          optRows.push(orow);
          opts += this.offerRow(orow, 0);
        }
      }
      if (opts) html += '<h3 class="dpa-offer__heading">' + escapeHtml(t.ofOptions) + '</h3><ul class="dpa-offer__list dpa-offer__list--options">' + opts + '</ul>';
    }
    html += '<p class="dpa-offer__note">' + escapeHtml(t.ofNote) + '</p>' +
      '<a class="dpa-btn dpa-btn--outline dpa-offer__new" href="' + escapeAttr(this.newAnalysisHref()) + '">' + escapeHtml(t.ofNew) + '</a></div>';
    this.el.results.innerHTML = html;
    this.offerTotal();
    // v2.3.2 (bug hunt DMG-A-10): the options too — they showed prices stored up to 30 days ago under a note that
    // says "current site data"; only the picked rows count in the total
    this.refreshLive(rows.length ? rows : optRows);
  };

  DamageAnalyzer.prototype.offerRow = function (row, n) {
    var t = this.t, p = row.p || {};
    var img = safeUrl(p.image);
    var href = this.productHref({ handle: p.handle });
    var price = { amount: String(p.price || ''), currencyCode: p.currency || this.currency };
    var amount = parseFloat(price.amount);
    return '<li class="dpa-offer__item' + (p.available === false ? ' is-soldout' : '') + '" data-dpa-offer-item="' + escapeAttr(row.ix) + '">' +
      '<a class="dpa-offer__link" href="' + escapeAttr(href) + '" target="_blank" rel="noopener">' +
      (img ? '<img class="dpa-offer__img" src="' + escapeAttr(img) + '" alt="" loading="lazy" decoding="async" width="72" height="72">'
           : '<span class="dpa-offer__img dpa-offer__img--missing" aria-hidden="true">—</span>') +
      '<span class="dpa-offer__body">' +
      '<span class="dpa-offer__part">' + (n ? n + '. ' : '') + escapeHtml(row.part + (row.hidden ? ' (' + t.mLikely + ')' : '')) + '</span>' +
      '<span class="dpa-offer__name">' + escapeHtml(p.title || '') + '</span>' +
      (p.vendor ? '<span class="dpa-offer__vendor">' + escapeHtml(p.vendor) + '</span>' : '') +
      '<span class="dpa-offer__flag" data-dpa-offer-flag' + (p.available === false ? '' : ' hidden') + '>' + escapeHtml(p.available === false ? t.soldOut : '') + '</span>' +
      '</span>' +
      '<span class="dpa-offer__price" data-dpa-offer-price>' + escapeHtml(isFinite(amount) && amount > 0 ? this.formatPrice(price) : t.mTbd) + '</span>' +
      '</a></li>';
  };

  /** Sum of the picked rows: the live price where the store answered, otherwise the price stored with the analysis. */
  DamageAnalyzer.prototype.offerTotal = function () {
    var v = this._offerView, box = this.el.results;
    if (!v || !box) return;
    var total = 0, tbd = false, currency = '';
    for (var i = 0; i < v.rows.length; i++) {
      var row = v.rows[i], live = v.live[row.ix];
      if (live && live.gone) continue;
      var amount = live && isFinite(live.price) ? live.price : parseFloat(row.p && row.p.price);
      if (isFinite(amount) && amount > 0) { total += amount; if (!currency) currency = (row.p && row.p.currency) || ''; }
      else tbd = true;
    }
    var el = box.querySelector('[data-dpa-offer-total]'), tbdEl = box.querySelector('[data-dpa-offer-tbd]');
    if (el) el.textContent = this.formatPrice({ amount: String(Math.round(total * 100) / 100), currencyCode: currency || this.currency });
    if (tbdEl) tbdEl.hidden = !tbd;
  };

  /**
   * Live price and stock from the store itself (/products/<handle>.js, same
   * origin). A price in another currency (a different market) is not mixed in;
   * a product removed from the store is marked and left out of the total.
   */
  DamageAnalyzer.prototype.refreshLive = function (rows) {
    var self = this, t = this.t, v = this._offerView;
    if (typeof fetch !== 'function') return;
    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    // one lookup per product, however many damages list it (v2.3.2: the options list repeats products)
    var once = {};
    rows = rows.filter(function (row) { return once[row.ix] ? false : (once[row.ix] = true); });
    rows.slice(0, LIVE_PRICE_MAX).forEach(function (row) {
      var h = row.p && row.p.handle;
      if (typeof h !== 'string' || !/^[^\/?#\s]{1,255}$/.test(h)) return;
      fetch(self.rootUrl + '/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (r.status === 404) return { gone: true };
          return r.ok ? r.json() : null;
        })
        .then(function (d) {
          if (!d || self._offerView !== v) return;
          // every row of this product (an option may be listed under two damages)
          var lis = self.el.results.querySelectorAll('[data-dpa-offer-item="' + row.ix + '"]');
          if (!lis.length) return;
          var cur = (row.p && row.p.currency) || self.currency;
          var old = parseFloat(row.p && row.p.price);
          var live = d.gone ? { gone: true } : {};
          if (!d.gone && typeof d.price === 'number' && isFinite(d.price) && d.price > 0 && (!active || active === cur)) live.price = Math.round(d.price) / 100;
          v.live[row.ix] = live;
          var soldOut = !d.gone && d.available === false;
          var changed = isFinite(live.price) && Math.abs(old - live.price) >= 0.01;
          var paint = function (li) {
            var flag = li.querySelector('[data-dpa-offer-flag]'), priceEl = li.querySelector('[data-dpa-offer-price]');
            var setFlag = function (text) { if (flag) { flag.textContent = text; flag.hidden = !text; } };
            if (d.gone) {
              li.classList.add('is-gone');
              setFlag(t.ofGone);
              return;
            }
            if (isFinite(live.price) && priceEl) {
              priceEl.textContent = self.formatPrice({ amount: String(live.price), currencyCode: cur });
              if (isFinite(old) && old > 0 && Math.abs(old - live.price) >= 0.01) {
                var was = document.createElement('s');
                was.className = 'dpa-offer__was';
                was.textContent = self.formatPrice({ amount: String(old), currencyCode: cur });
                priceEl.appendChild(was);
              }
            }
            if (soldOut) li.classList.add('is-soldout'); else li.classList.remove('is-soldout');
            setFlag(soldOut ? t.soldOut : changed ? t.ofPriceNow : '');
          };
          for (var k = 0; k < lis.length; k++) paint(lis[k]);
          self.offerTotal();
        })
        .catch(function () { /* the stored price stays */ });
    });
  };

  DamageAnalyzer.prototype.renderSearchFallback = function (partName) {
    var v = this._shownVehicle || this.state.vehicle;
    var query = v ? v.model + ' ' + v.year + ' ' + partName : partName;
    var url = this.searchUrl + '?q=' + encodeURIComponent(query);
    return '<a class="dpa-search-link" href="' + escapeAttr(url) + '" target="_blank" rel="noopener">' + escapeHtml(this.t.searchCatalog) + '</a>';
  };

  DamageAnalyzer.prototype.formatPrice = function (price) {
    if (!price || !price.amount) return '';
    var amount = parseFloat(price.amount);
    if (!isFinite(amount)) return '';
    var currency = price.currencyCode || this.currency;
    var symbol = currency === 'GEL' ? '₾' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' ';
    var digits = Math.round(amount) === amount ? amount.toFixed(0) : amount.toFixed(2);
    // 1250 → "1 250" (the order total easily passes a thousand)
    return symbol + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  // ─── Photo preparation ──────────────────────────────────
  // Re-encode every photo: strips EXIF (GPS, device data), applies the camera
  // orientation, caps the long edge. Undecodable files (e.g. HEIC on desktop
  // Chrome) go as-is — the worker validates the real format by its bytes.
  DamageAnalyzer.prototype.prepareImage = function (file) {
    return decodeImage(file).then(function (src) {
      var sw = src.width || src.naturalWidth, sh = src.height || src.naturalHeight;
      if (!sw || !sh) throw new Error('empty image');
      var scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
      var w = Math.max(1, Math.round(sw * scale));
      var h = Math.max(1, Math.round(sh * scale));
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d');
      if (!ctx || !canvas.toBlob) throw new Error('no canvas');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(src, 0, 0, w, h);
      if (src.close) src.close();
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) {
          canvas.width = canvas.height = 0; // release the bitmap memory now (mobile)
          resolve(blob && blob.size > 0 ? blob : file);
        }, 'image/jpeg', JPEG_QUALITY);
      });
    }).catch(function () { return file; });
  };

  /**
   * An upright decoded image: createImageBitmap with the EXIF orientation;
   * where that option is not supported (iOS 15, older Chrome/Firefox) an <img>
   * element, which applies the EXIF orientation by default; a plain bitmap
   * (orientation not guaranteed) only as the last resort.
   */
  function decodeImage(file) {
    var viaBitmap = function (opts) {
      if (typeof createImageBitmap !== 'function') return Promise.reject(new Error('no createImageBitmap'));
      try { return opts ? createImageBitmap(file, opts) : createImageBitmap(file); } catch (e) { return Promise.reject(e); }
    };
    var viaImg = function () {
      return new Promise(function (resolve, reject) {
        if (!window.URL || !URL.createObjectURL) return reject(new Error('no object URLs'));
        var url = URL.createObjectURL(file);
        var img = new Image();
        var done = function (ok) {
          clearTimeout(timer);
          img.onload = img.onerror = null;
          URL.revokeObjectURL(url);
          if (ok) resolve(img); else reject(new Error('undecodable'));
        };
        var timer = setTimeout(function () { done(false); }, 15000); // a decoder that never answers
        img.onload = function () { done(true); };
        img.onerror = function () { done(false); };
        img.src = url;
      });
    };
    return viaBitmap({ imageOrientation: 'from-image' })
      .catch(viaImg)
      .catch(function () { return viaBitmap(null); });
  }

  // ─── Utilities ──────────────────────────────────────────
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
  /**
   * "500 702 500" / "+995 500-702-500" / "00995 500…" / "+995 (0) 500…" → "995500702500"
   * (wa.me wants the full international number, digits only). Anything else → '' (no button).
   */
  function waDigits(raw) {
    var d = String(raw || '').replace(/\(0\)/g, '').replace(/\D+/g, '').replace(/^00/, '');
    if (/^5\d{8}$/.test(d)) d = '995' + d;
    return /^[1-9]\d{9,14}$/.test(d) ? d : '';
  }
  /** "995500702500" → "+995 500 70 25 00" (the number shown under the button label). */
  function waPretty(d) {
    var m = /^995(\d{3})(\d{2})(\d{2})(\d{2})$/.exec(d);
    return m ? '+995 ' + m[1] + ' ' + m[2] + ' ' + m[3] + ' ' + m[4] : '+' + d;
  }
  /** Shortens text without splitting a surrogate pair (a split one makes encodeURIComponent throw). */
  function clip(s, max) {
    if (s.length <= max) return s;
    var cut = max - 1, c = s.charCodeAt(cut - 1);
    if (c >= 0xd800 && c <= 0xdbff) cut--;
    return s.slice(0, cut) + '…';
  }
  /** Every damaged part without a picked product, and why — the same list in the message and in the order view. */
  function missingParts(t, groups) {
    var out = [], seen = {};
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.picked || seen[g.part]) continue;
      seen[g.part] = true;
      var why = g.used ? t.mUsed : !g.count ? t.mNotFound : !g.inStock ? t.mSoldOut : g.uncertain ? t.mSideTbd : t.mNotSelected;
      out.push({ part: g.part + (g.hidden ? ' (' + t.mLikely + ')' : ''), why: why });
    }
    return out;
  }
  /** Product indexes ↔ hex bitmask, 4 indexes per digit, lowest first: [0, 2, 5] → "52". */
  function toMask(bits) {
    var d = [];
    for (var i = 0; i < bits.length; i++) {
      var k = bits[i] >> 2;
      while (d.length <= k) d.push(0);
      d[k] |= 1 << (bits[i] & 3);
    }
    var s = '';
    for (var j = 0; j < d.length; j++) s += d[j].toString(16);
    return s || '0';
  }
  function fromMask(hex) {
    var out = [];
    for (var k = 0; k < hex.length; k++) {
      var v = parseInt(hex.charAt(k), 16);
      for (var j = 0; j < 4; j++) if (v & (1 << j)) out.push(k * 4 + j);
    }
    return out;
  }
  /** Local "23.09.2026 14:05". */
  function fmtDate(d) {
    var p = function (n) { return (n < 10 ? '0' : '') + n; };
    return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
  }
  var waMem = null;
  function waRead() {
    var j = null;
    try { j = JSON.parse(window.localStorage.getItem(WA_STORE) || 'null'); } catch (e) { j = null; }
    if (!j || typeof j !== 'object' || !j.sent || typeof j.sent !== 'object') j = waMem || { last: 0, sent: {} };
    return j;
  }
  function waWrite(j) {
    waMem = j;
    try { window.localStorage.setItem(WA_STORE, JSON.stringify(j)); } catch (e) { /* private mode: this page's memory only */ }
  }
  function waState(key) {
    var j = waRead(), now = Date.now(), last = +j.last || 0, n = +j.sent[key] || 0;
    if (last > now) last = 0; // a clock set back never locks the button
    var wait = Math.ceil((last + WA_COOLDOWN_S * 1000 - now) / 1000);
    return { sends: n, full: n >= WA_MAX_PER_ORDER, wait: wait > 0 ? wait : 0 };
  }
  function waRecord(key) {
    var j = waRead(), keys = Object.keys(j.sent);
    if (keys.length >= 20) delete j.sent[keys[0]];
    j.sent[key] = (+j.sent[key] || 0) + 1;
    j.last = Date.now();
    waWrite(j);
  }
  /** v2.3.2 (audit S10): the analyze endpoint — https://, or plain http only on this computer (a local preview). */
  function workerUrlOk(u) {
    return /^https:\/\/[^\/\\\s]/i.test(u) || /^http:\/\/(?:localhost|127\.0\.0\.1|\[::1\])(?::\d{1,5})?(?:[\/?#]|$)/i.test(u);
  }
  /** v2.3.2 (DMG-A-02): a HEIC/HEIF photo — by its type, or by its name when the browser leaves the type empty. */
  function isHeic(file) {
    return HEIC_TYPE.test(String((file && file.type) || '').toLowerCase()) || /\.(heic|heif)$/i.test(String((file && file.name) || ''));
  }
  /** v2.3.2 (DMG-A-11): the visitor asked for reduced motion. */
  function reducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }
  /** https:// or protocol-relative (Shopify's image_url) only — no plain http, no "///" or "/\" tricks. */
  function safeUrl(u) {
    var s = String(u == null ? '' : u).trim();
    return /^(https:)?\/\/[^\/\\\s]/i.test(s) ? s : '';
  }
  /** Upper case; I, O and Q (never in a VIN) read as the 1 and 0 they are mistaken for — 2.2 deleted them and shifted the VIN. */
  function cleanVin(s) {
    return String(s || '').toUpperCase().replace(/[OQ]/g, '0').replace(/I/g, '1').replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
